import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { toast } from "sonner";
import { createUser } from "@/api/user/create-account";
import { useMutation } from "@tanstack/react-query";
import { Background } from "@/components/Background";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import talyko from "@/assets/TalykoLogo.png";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const createAccountSchema = z.object({
  firstName: z.string({ message: "Invoice number is required" }),
  lastName: z.string({ message: "Enter Service Description " }),
  email: z.string().email({ message: "Must be a valid email address" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" }),
});

type CreateAccountType = z.infer<typeof createAccountSchema>;

export function RegisterAccount() {
  const navigate = useNavigate();

  const { register, handleSubmit, reset } = useForm<CreateAccountType>({
    resolver: zodResolver(createAccountSchema),
  });

  const { mutateAsync: CreateUserFn } = useMutation({
    mutationKey: ["users"],
    mutationFn: createUser,
  });

  // const session = User?.user;

  async function handleCreateAccount(data: CreateAccountType) {
    try {
      const { status } = await CreateUserFn({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      });
      if (status === 201) {
        toast.success("User created successfully");
        navigate("/company");
        reset();
      }
    } catch (err) {
      console.log(err);
      toast.error("Error creating user");
    }
  }

  return (
    <div className="relative">
      <Background>
        {/* First quadrant with SVG curve */}
        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full sm:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#16A34A"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 800 0 C 700 400, 100 400, 0 800 L 0 0 Z"
              />
            </svg>
          </div>
          {/* Logo placed on top of the curve */}
          <div className="flex justify-center p-4 sm:absolute sm:left-10 sm:top-4 sm:mt-0 sm:p-0">
            <img
              className="h-14 md:h-14 xl:h-16"
              src={talyko}
              alt="Talyko Logo"
            />
          </div>
        </div>

        {/* Other quadrants */}
        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full md:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#A2D8D8"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 150 0 C 200 300, 600 300, 800 600 L 800 0 Z"
              />
            </svg>
          </div>
        </div>

        <div></div>

        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full sm:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#F8CF2C"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 0 800 C 600 400, 450 200, 800 150 L 800 800 Z"
              />
            </svg>
          </div>
        </div>
      </Background>
      <form onSubmit={handleSubmit(handleCreateAccount)}>
        <Card className="absolute left-1/2 top-96 mx-auto h-[540px] w-[380px] -translate-x-1/2 -translate-y-1/2 space-y-7 rounded-[2.5rem] border border-black p-5 font-Hemmers text-black sm:top-1/2 md:w-[450px] xl:w-[500px]">
          <div className="absolute inset-0 h-10 w-full rounded-tl-full rounded-tr-full bg-[#F8CF2C] p-0">
            <div className="absolute inset-6 inset-x-5 flex items-center justify-end gap-4">
              <span className="flex size-3 items-center rounded-full bg-black" />
              <span className="flex size-3 items-center rounded-full bg-black" />
              <span className="flex size-3 items-center rounded-full bg-black" />
            </div>
          </div>
          <CardHeader className="flex items-center text-2xl md:text-3xl">
            Create an Account
            <div></div>
            <Progress value={33} />
          </CardHeader>
          <CardContent>
            <div className="w-full space-y-4">
              <Input
                className="bg-[#ECECEC]"
                placeholder="First Name"
                {...register("firstName")}
              />
              <Input
                className="bg-[#ECECEC]"
                placeholder="Last Name"
                {...register("lastName")}
              />
              <Input
                className="bg-[#ECECEC]"
                placeholder="Email"
                {...register("email")}
              />
              <Input
                className="bg-[#ECECEC]"
                placeholder="Password"
                type="password"
                {...register("password")}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full bg-blue-950 hover:bg-blue-900"
              type="submit"
            >
              Next
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
